import React, { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { AuthContext } from '../../contexts/AuthContext';
import { BookOpen, Users, Clock, BookMarked, ImageIcon, Bird } from 'lucide-react';
import { motion } from 'framer-motion';
import Footer from '../Website/Landing/sections/Footer';
import FeatureCostBadge from '../../components/common/FeatureCostBadge';
import '../../kids-theme.css'; // Keep this import for any existing theme styles
import CreditsDisplay from '../../components/common/CreditsDisplay';

// Add custom animation for the logo
const logoAnimationStyle = `
  @keyframes float {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-5px); }
  }
  .logo-float {
    animation: float 3s ease-in-out infinite;
  }
`;

const Home = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [sessionId, setSessionId] = useState(null);
  
  useEffect(() => {
    if (!user && !sessionId) {
      const newSessionId = localStorage.getItem('sessionId') || uuidv4();
      localStorage.setItem('sessionId', newSessionId);
      setSessionId(newSessionId);
    }
  }, [user, sessionId]);

  // Define use cases/tiles for our grid
  const useCases = [
    {
      title: "Quick Stories",
      description: "Experience the magic of AI storytelling! Our advanced AI crafts enchanting tales in seconds, complete with voice narration by KuchiPuchi. Perfect for bedtime, car rides, or anytime you need a sprinkle of imagination. Listen as your story comes alive with delightful narration.",
      icon: <Clock className="h-12 w-12 text-white" />,
      path: "/app/tools/quick-story",
      gradient: "from-[#3A0CA3] to-[#5E17EB]",
      comingSoon: false,
      featureKey: "QuickStories"
    },
    {
      title: "Personalized Storybook",
      description: "Step into a world where your imagination comes alive! Our AI wizard transforms your ideas into beautifully illustrated storybooks with custom artwork and interactive elements. Each page can be narrated by KuchiPuchi, bringing your story to life with enchanting voices.",
      icon: <BookOpen className="h-12 w-12 text-white" />,
      path: "/app/tools/story",
      gradient: "from-[#F97316] to-[#FACC15]",
      comingSoon: false,
      featureKey: "StoryBook"
    },
    {
      title: "Fable Whisperer",
      description: "Let KuchiPuchi's magical voice bring your stories to life! Record your own voice with our voice cloning feature to create a uniquely personal narration experience. Enjoy professional character voices, delightful sound effects, and emotion-rich storytelling that will captivate young hearts.",
      icon: <Bird className="h-12 w-12 text-white" />,
      path: "/app/tools/story-narrator",
      gradient: "from-[#9333EA] to-[#EC4899]",
      comingSoon: false,
      isNew: false,
      featureKey: "StoryNarrator"
    },
    {
      title: "Personalized PhotoBook",
      description: "Turn your precious photos into enchanting adventures! Watch as your child becomes the star of their very own magical tale, with AI-enhanced illustrations that blend reality with fantasy. KuchiPuchi's warm narration makes every personal story an immersive experience.",
      icon: <ImageIcon className="h-12 w-12 text-white" />,
      path: "/app/tools/photo-books",
      gradient: "from-[#0891B2] to-[#2DD4BF]",
      comingSoon: true,
      featureKey: "PhotoBook"
    },
    {
      title: "Character Builder",
      description: "Unleash your creativity in our magical character workshop! Design unique heroes, lovable sidekicks, and memorable friends with our AI-powered character creator. Each character comes with their own unique voice, performed by KuchiPuchi in your stories.",
      icon: <Users className="h-12 w-12 text-white" />,
      path: "/app/tools/character",
      gradient: "from-[#7209B7] to-[#B5179E]",
      comingSoon: true,
    },
    {
      title: "My Library",
      description: "Welcome to your enchanted collection of tales! Discover all your magical creations in one cozy corner, organized beautifully for endless storytelling adventures. Every story in your library can be narrated by KuchiPuchi anytime you want to listen.",
      icon: <BookMarked className="h-12 w-12 text-white" />,
      path: "/app/library",
      gradient: "from-[#059669] to-[#10B981]",
      comingSoon: true
    },
    {
      title: "Kids Learning",
      description: "Explore fun and interactive learning adventures! Dive into English Literacy, Maths, Science, Geography, History, and more — all designed to spark curiosity and make learning magical for young minds.",
      icon: <BookOpen className="h-12 w-12 text-white" />,
      path: "/kids-learning/index.html",
      gradient: "from-[#0369A1] to-[#38BDF8]",
      comingSoon: false,
      featureKey: null,
      isExternal: true
    }
  ];

  // Add a helper function at the top of the component to get text color based on gradient
  const getTextColor = (gradient) => {
    if (gradient.includes("3A0CA3")) return "[#5E17EB]";
    if (gradient.includes("F97316")) return "[#F97316]";
    if (gradient.includes("9333EA")) return "[#9333EA]";
    if (gradient.includes("0891B2")) return "[#0891B2]";
    if (gradient.includes("7209B7")) return "[#B5179E]";
    if (gradient.includes("059669")) return "[#059669]";
    return "[#FF5757]"; // Default
  };

  // Handler for tile clicks
  const handleTileClick = (path, comingSoon, isExternal) => {
    if (comingSoon) {
      return;
    }
    if (isExternal) {
      window.location.href = path;
      return;
    }
    navigate(path);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Inject the custom animation style */}
      <style>{logoAnimationStyle}</style>
      
      {/* Breadcrumb navigation */}
      {/* <div className="absolute top-4 left-4 z-50">
        <div className="flex items-center space-x-2 bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full shadow-md">
          <button 
            onClick={() => navigate('/')} 
            className="flex items-center hover:underline transition-all group"
          >
            <HomeIcon className="w-5 h-5 mr-1" />
            <span>Home</span>
          </button>
        </div>
      </div> */}
      
      {/* Header with gradient matching the landing page */}
      <div className="py-12 bg-gradient-to-r from-[#FF5757] to-[#FFAA00]">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-center space-y-6">
            <div className="relative">
              {/* Optional decorative elements */}
              <div className="absolute -top-6 left-1/2 transform -translate-x-1/2">
                <div className="w-24 h-1 bg-white/30 rounded-full"></div>
              </div>
              
              {/* Heading with logo to the left */}
              <div className="flex items-center justify-center gap-6 mb-4">
                <div className="flex items-center logo-float">
                  <img 
                    src="/images/logo/kuchipuchi_logo1.png" 
                    alt="Kuchipuchi Logo" 
                    className="h-16 w-auto object-contain drop-shadow-lg"
                  />
                  <span className="ml-2 px-2 py-1 text-xs font-bold bg-white/20 text-white rounded-full backdrop-blur-sm border border-white/30">
                    BETA
                  </span>
                </div>
                <h1 className="text-4xl md:text-5xl font-bold text-white tracking-tight">
                  Welcome to KuchiPuchi Studio
                </h1>
              </div>
              
              <div className="flex flex-col items-center gap-3">
                <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed font-medium">
                  What magical story would you like to create today?
                </p>
                
                {user && (
                  <div className="inline-flex items-center px-3 py-1.5 bg-white rounded-full border border-[#D1E2FB] shadow-lg hover:shadow-xl transition-all">
                    <CreditsDisplay 
                      className="text-[#5E60CE]" 
                      hideIcon={false}
                    />
                    <span className="ml-2 text-sm text-[#1A202C]/70">remaining</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        
        {/* Optional: Add decorative elements */}
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-white/0 via-white/20 to-white/0"></div>
      </div>

      {/* Main content with tiles */}
      <div className="py-16 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Star pattern background similar to landing page */}
          <div className="relative z-10">
            <div className="absolute inset-0 opacity-10 bg-[radial-gradient(white_1px,transparent_1px)] bg-[length:16px_16px]"></div>
            
            {/* Desktop View - Grid Layout */}
            <motion.div
              className="hidden md:grid md:grid-cols-2 gap-8 relative z-20"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, staggerChildren: 0.1 }}
            >
              {useCases.map((useCase, index) => (
                <motion.div
                  key={index}
                    onClick={() => handleTileClick(useCase.path, useCase.comingSoon, useCase.isExternal)}
                  className={`bg-gradient-to-r ${useCase.gradient} rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 cursor-pointer relative overflow-hidden group`}
                  whileHover={{
                    scale: 1.05,
                    y: -10,
                    transition: { type: "spring", stiffness: 300, damping: 20 }
                  }}
                  whileTap={{ scale: 0.98 }}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  {/* Star pattern within each tile */}
                  <div className="absolute inset-0 opacity-10 bg-[radial-gradient(white_1px,transparent_1px)] bg-[length:16px_16px]"></div>
                  
                  {useCase.isNew && (
                    <div className="absolute top-4 right-4 bg-gradient-to-r from-white to-white/80 text-[#EC4899] text-xs font-bold px-2 py-1 rounded-full shadow-lg">
                      NEW FEATURE
                    </div>
                  )}
                  
                  <div className="flex items-start gap-6">
                    <div className="bg-white/10 p-4 rounded-xl backdrop-blur-sm">
                      {useCase.icon}
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex justify-between items-center mb-3">
                        <h3 className="text-2xl font-bold text-white">{useCase.title}</h3>
                        {useCase.featureKey && (
                          <FeatureCostBadge 
                            feature={useCase.featureKey} 
                          />
                        )}
                      </div>
                      <p className="text-white/90 mb-6">{useCase.description}</p>
                      
                      <div className="flex justify-end">
                        <button 
                          className={`bg-white text-${useCase.comingSoon ? 'gray-400' : getTextColor(useCase.gradient)} py-2 px-6 rounded-full font-medium group-hover:scale-105 transition-all shadow-lg flex items-center`}
                          disabled={useCase.comingSoon}
                        >
                          {useCase.comingSoon ? 'Coming Soon' : 'Get Started'}
                          {!useCase.comingSoon && (
                            <span className="ml-2 group-hover:translate-x-1 transition-transform">→</span>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            {/* Mobile View - 2 Column Grid */}
            <motion.div
              className="md:hidden relative z-20"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, staggerChildren: 0.1 }}
            >
              <div className="grid grid-cols-2 gap-4">
                {useCases.map((useCase, index) => (
                  <motion.div
                    key={index}
                      onClick={() => handleTileClick(useCase.path, useCase.comingSoon, useCase.isExternal)}
                    className={`bg-gradient-to-r ${useCase.gradient} rounded-xl p-4
                      shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer relative overflow-hidden group`}
                    whileHover={{
                      scale: 1.05,
                      y: -5,
                      transition: { type: "spring", stiffness: 300, damping: 20 }
                    }}
                    whileTap={{ scale: 0.98 }}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    {/* Star pattern within each tile */}
                    <div className="absolute inset-0 opacity-10 bg-[radial-gradient(white_1px,transparent_1px)] bg-[length:16px_16px]"></div>
                    
                    <div className="flex justify-between items-start">
                      <div className="bg-white/10 p-2 rounded-lg backdrop-blur-sm w-fit mb-3">
                        {React.cloneElement(useCase.icon, { className: 'h-8 w-8 text-white' })}
                      </div>
                      
                      {useCase.featureKey && (
                        <FeatureCostBadge 
                          feature={useCase.featureKey} 
                          className="text-[10px]"
                        />
                      )}
                    </div>
                    
                    {useCase.isNew && (
                      <div className="absolute top-2 right-2 bg-white text-[#EC4899] text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                        NEW
                      </div>
                    )}
                    
                    <h3 className="text-lg font-bold text-white mb-2">{useCase.title}</h3>
                    <p className="text-white/90 text-xs mb-4 line-clamp-2">{useCase.description}</p>
                    
                    <div className="flex justify-end">
                      <button 
                        className={`bg-white text-${useCase.comingSoon ? 'gray-400' : getTextColor(useCase.gradient)} py-1.5 px-3 rounded-full 
                          text-xs font-medium group-hover:scale-105 transition-all shadow-md flex items-center`}
                        disabled={useCase.comingSoon}
                      >
                        {useCase.comingSoon ? 'Soon' : 'Start'}
                        {!useCase.comingSoon && (
                          <span className="ml-1 group-hover:translate-x-1 transition-transform">→</span>
                        )}
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Add Footer */}
      <Footer />
    </div>
  );
};

export default Home;
